/*
  Cart drawer extras — Squarbody Syndicate
  CART DRAWER ONLY. Builds on Motion theme's existing Ajax cart
  (theme.cart / theme.CartForm / theme.CartDrawer). The /cart page and all
  other areas are untouched: everything here is scoped to #CartDrawerForm.

  Features:
  - Line item remove buttons (injected into drawer line items only)
  - Free shipping progress bar (hidden when restricted products are in the cart)
  - Related product upsells (native Shopify automatic recommendations, intent=related)
  - Gift note persisted as a cart attribute
  - Order add-ons (qty locked to 1, add-on-only checkout gate)
  - Shipping notice acknowledgment (checkout disabled until the box is ticked)

  All listeners are bound exactly once. The theme only replaces the drawer's
  [data-products] container on rerender, so dynamic content inside it is
  handled through event delegation plus a MutationObserver that re-decorates
  line items after each rebuild.
*/
(function() {
  'use strict';

  if (!window.theme || !theme.cart) {
    return;
  }

  // The theme never initializes the cart drawer on the cart page — mirror that guard.
  if (document.body.classList.contains('template-cart')) {
    return;
  }

  var form = document.getElementById('CartDrawerForm');
  if (!form) {
    return;
  }

  var GIFT_NOTE_ATTR = 'Gift note';

  var config = { addonIds: [], freeShipExcludedIds: [], removeLabel: 'Remove' };
  var configEl = form.querySelector('[data-cart-drawer-config]');
  if (configEl) {
    try {
      var parsed = JSON.parse(configEl.textContent);
      Object.keys(config).forEach(function(key) {
        if (parsed[key] != null) {
          config[key] = parsed[key];
        }
      });
    } catch (e) {
      // keep defaults
    }
  }

  var addonIds = (config.addonIds || []).map(Number).filter(Boolean);
  var excludedIds = (config.freeShipExcludedIds || []).map(Number).filter(Boolean);

  var els = {
    products: form.querySelector('[data-products]'),
    checkoutBtn: form.querySelector('.cart__checkout'),
    checkoutTotal: form.querySelector('[data-checkout-total]'),
    addonWarning: form.querySelector('[data-addon-warning]'),
    addonsWrap: form.querySelector('[data-cart-addons]'),
    shipbar: form.querySelector('[data-cart-shipbar]'),
    shipbarMsg: form.querySelector('[data-shipbar-msg]'),
    shipbarFill: form.querySelector('[data-shipbar-fill]'),
    upsells: form.querySelector('[data-cart-upsells]'),
    upsellList: form.querySelector('[data-upsell-list]'),
    giftNoteToggle: form.querySelector('[data-gift-note-toggle]'),
    giftNoteField: form.querySelector('[data-gift-note-field]'),
    giftNoteText: form.querySelector('[data-gift-note-text]'),
    giftNoteCount: form.querySelector('[data-gift-note-count]'),
    noticeAck: form.querySelector('[data-notice-ack]')
  };

  var state = {
    cart: null,
    addonsOnly: false,
    upsellSourceId: null,
    upsellProducts: [],
    giftNoteDirty: false,
    removing: false,
    clampInFlight: {}
  };

  function formatMoney(cents) {
    if (theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function debounce(fn, wait) {
    var t;
    return function() {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function() { fn.apply(null, args); }, wait);
    };
  }

  function isAddon(productId) {
    return addonIds.indexOf(Number(productId)) !== -1;
  }

  function rebuildThemeCart() {
    // theme.CartForm listens for this and rebuilds [data-products], subtotal,
    // discounts, savings and the cart count bubble through its own logic.
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  /*============================================================================
    Line item decoration (drawer only) — remove buttons + add-on qty locks.
    The shared cart-item markup is not modified; the drawer decorates its own
    copy after every rebuild via MutationObserver.
  ==============================================================================*/
  function decorateLineItems() {
    if (!els.products) {
      return;
    }

    var itemsByKey = {};
    if (state.cart) {
      state.cart.items.forEach(function(item) {
        itemsByKey[item.key] = item;
      });
    }

    els.products.querySelectorAll('.cart__item[data-key]').forEach(function(itemEl) {
      var key = itemEl.dataset.key;
      var qtyCell = itemEl.querySelector('.cart__item--qty');

      // Inject remove button once per rendered line item
      if (qtyCell && !itemEl.querySelector('.cart__remove')) {
        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'cart__remove';
        removeBtn.dataset.key = key;
        removeBtn.textContent = config.removeLabel;
        qtyCell.appendChild(removeBtn);
      }

      // Lock add-on line items to quantity 1 (matched by product ID, never SKU)
      var item = itemsByKey[key];
      if (item && isAddon(item.product_id)) {
        itemEl.classList.add('cart__item--addon');
        var input = itemEl.querySelector('.js-qty__num');
        var plus = itemEl.querySelector('.js-qty__adjust--plus');
        if (input) {
          input.setAttribute('readonly', 'readonly');
          input.setAttribute('max', '1');
        }
        if (plus) {
          plus.disabled = true;
          plus.setAttribute('aria-disabled', 'true');
        }
      }
    });
  }

  if (els.products) {
    new MutationObserver(decorateLineItems).observe(els.products, { childList: true });
  }

  /*============================================================================
    Remove buttons — delegated once on the drawer form
  ==============================================================================*/
  form.addEventListener('click', function(evt) {
    var btn = evt.target.closest('.cart__remove');
    if (!btn || state.removing) {
      return;
    }

    var key = btn.dataset.key;
    if (!key) {
      return;
    }

    state.removing = true;
    var item = btn.closest('.cart__item');
    if (item) {
      item.classList.add('is-removing');
    }

    theme.cart.changeItem(key, 0)
      .then(function(cartText) {
        state.removing = false;
        var cart = JSON.parse(cartText);
        if (cart.status === 422) {
          alert(cart.message || cart.description);
          if (item) { item.classList.remove('is-removing'); }
          return;
        }
        rebuildThemeCart();
        renderExtras(cart);
      })
      .catch(function() {
        state.removing = false;
        if (item) { item.classList.remove('is-removing'); }
      });
  });

  /*============================================================================
    Shared add-to-cart used by upsells and add-ons (Ajax, variant ID only)
  ==============================================================================*/
  function addVariant(variantId, btn) {
    if (btn && (btn.disabled || btn.classList.contains('btn--loading'))) {
      return;
    }
    if (btn) {
      btn.classList.add('btn--loading');
    }

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    })
      .then(function(response) { return response.json(); })
      .then(function(data) {
        if (btn) {
          btn.classList.remove('btn--loading');
        }
        if (data.status === 422 || data.status === 404) {
          alert(data.description || data.message);
          return;
        }
        // The theme's existing listener rebuilds the drawer and keeps it open
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        refresh();
      })
      .catch(function() {
        if (btn) {
          btn.classList.remove('btn--loading');
        }
        refresh();
      });
  }

  /*============================================================================
    Free shipping progress
  ==============================================================================*/
  function renderShipbar(cart) {
    if (!els.shipbar) {
      return;
    }

    var threshold = parseInt(els.shipbar.dataset.threshold, 10) || 0;
    var restricted = cart.items.some(function(item) {
      return excludedIds.indexOf(item.product_id) !== -1;
    });

    if (restricted || cart.item_count === 0 || threshold <= 0) {
      els.shipbar.classList.add('hide');
      return;
    }

    els.shipbar.classList.remove('hide');

    var remaining = threshold - cart.total_price;
    var progress = Math.min(100, Math.round((cart.total_price / threshold) * 100));

    if (remaining <= 0) {
      els.shipbarMsg.innerHTML = escapeHtml(els.shipbar.dataset.success);
      els.shipbar.classList.add('cart-shipbar--complete');
    } else {
      els.shipbarMsg.innerHTML = escapeHtml(els.shipbar.dataset.message).replace('[remaining]', formatMoney(remaining));
      els.shipbar.classList.remove('cart-shipbar--complete');
    }

    els.shipbarFill.style.width = progress + '%';
  }

  /*============================================================================
    Upsells — native Shopify automatic related recommendations (intent=related)
  ==============================================================================*/
  function renderUpsellList(cart) {
    var inCart = cart.items.map(function(item) { return item.product_id; });
    var visible = state.upsellProducts.filter(function(p) {
      return p.available && inCart.indexOf(p.id) === -1 && !isAddon(p.id);
    }).slice(0, 4);

    if (!visible.length) {
      els.upsells.classList.add('hide');
      return;
    }

    var buttonText = escapeHtml(els.upsells.dataset.buttonText);
    els.upsellList.innerHTML = visible.map(function(p) {
      var variant = (p.variants || []).filter(function(v) { return v.available; })[0];
      if (!variant) {
        return '';
      }
      var img = p.featured_image
        ? '<img src="' + escapeHtml(p.featured_image) + (p.featured_image.indexOf('?') === -1 ? '?' : '&') + 'width=120" alt="' + escapeHtml(p.title) + '" width="60" loading="lazy">'
        : '';
      return (
        '<div class="cart-upsell" data-product-id="' + p.id + '">' +
          '<a class="cart-upsell__image" href="' + escapeHtml(p.url) + '">' + img + '</a>' +
          '<div class="cart-upsell__info">' +
            '<a class="cart-upsell__title" href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a>' +
            '<div class="cart-upsell__price">' + formatMoney(variant.price) + '</div>' +
          '</div>' +
          '<button type="button" class="btn btn--small cart-upsell__add" data-upsell-add data-variant-id="' + variant.id + '">' + buttonText + '</button>' +
        '</div>'
      );
    }).join('');

    if (!els.upsellList.children.length) {
      els.upsells.classList.add('hide');
      return;
    }

    els.upsells.classList.remove('hide');
  }

  function renderUpsells(cart) {
    if (!els.upsells) {
      return;
    }

    if (cart.item_count === 0) {
      els.upsells.classList.add('hide');
      state.upsellSourceId = null;
      return;
    }

    // Recommend against the first non-add-on product in the cart
    var source = cart.items.filter(function(item) { return !isAddon(item.product_id); })[0] || cart.items[0];

    // Only skip the fetch when the last fetch for this seed succeeded —
    // a failed or empty fetch must retry on the next update/open.
    if (source.product_id === state.upsellSourceId) {
      renderUpsellList(cart);
      return;
    }

    var url = els.upsells.dataset.url + '.json?product_id=' + source.product_id + '&limit=10&intent=related';
    fetch(url, { credentials: 'same-origin' })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('recommendations ' + response.status);
        }
        return response.json();
      })
      .then(function(data) {
        state.upsellProducts = (data && data.products) || [];
        if (state.upsellProducts.length) {
          state.upsellSourceId = source.product_id;
        }
        renderUpsellList(cart);
      })
      .catch(function() {
        state.upsellSourceId = null;
        els.upsells.classList.add('hide');
      });
  }

  /*============================================================================
    Gift note — persisted as a cart attribute (not localStorage)
  ==============================================================================*/
  function updateGiftNoteCount() {
    if (!els.giftNoteCount || !els.giftNoteText) {
      return;
    }
    var template = els.giftNoteCount.dataset.counterText || '[count]/250';
    els.giftNoteCount.textContent = template.replace('[count]', els.giftNoteText.value.length);
  }

  var saveGiftNote = debounce(function() {
    if (!els.giftNoteText) {
      return;
    }
    var value = els.giftNoteText.value.slice(0, 250);
    theme.cart.updateAttribute(GIFT_NOTE_ATTR, value).then(function() {
      state.giftNoteDirty = false;
    });
  }, 500);

  function restoreGiftNote(cart) {
    if (!els.giftNoteText || state.giftNoteDirty || document.activeElement === els.giftNoteText) {
      return;
    }
    var saved = (cart.attributes && cart.attributes[GIFT_NOTE_ATTR]) || '';
    if (els.giftNoteText.value !== saved) {
      els.giftNoteText.value = saved;
    }
    updateGiftNoteCount();
  }

  if (els.giftNoteToggle) {
    els.giftNoteToggle.addEventListener('change', function() {
      if (this.checked) {
        els.giftNoteField.classList.remove('hide');
        els.giftNoteText.focus();
      } else {
        els.giftNoteField.classList.add('hide');
        els.giftNoteText.value = '';
        state.giftNoteDirty = false;
        theme.cart.updateAttribute(GIFT_NOTE_ATTR, '');
        updateGiftNoteCount();
      }
    });
  }

  if (els.giftNoteText) {
    els.giftNoteText.addEventListener('input', function() {
      state.giftNoteDirty = true;
      updateGiftNoteCount();
      saveGiftNote();
    });
    updateGiftNoteCount();
  }

  /*============================================================================
    Order add-ons
  ==============================================================================*/
  function renderAddons(cart) {
    if (!els.addonsWrap) {
      return;
    }

    var variantsInCart = cart.items.map(function(item) { return item.variant_id; });

    els.addonsWrap.querySelectorAll('[data-addon-add]').forEach(function(btn) {
      var added = variantsInCart.indexOf(Number(btn.dataset.variantId)) !== -1;
      btn.disabled = added;
      btn.textContent = added ? btn.dataset.addedText : btn.dataset.addText;
      btn.closest('[data-cart-addon]').classList.toggle('cart-addon--added', added);
    });
  }

  // If an add-on somehow exceeds qty 1 (e.g. added again from a product page), clamp it back
  function clampAddonQuantities(cart) {
    if (!addonIds.length) {
      return;
    }
    cart.items.forEach(function(item) {
      if (isAddon(item.product_id) && item.quantity > 1 && !state.clampInFlight[item.key]) {
        state.clampInFlight[item.key] = true;
        theme.cart.changeItem(item.key, 1).then(function(cartText) {
          delete state.clampInFlight[item.key];
          rebuildThemeCart();
          try {
            renderExtras(JSON.parse(cartText));
          } catch (e) { /* noop */ }
        });
      }
    });
  }

  function renderAddonGate(cart) {
    if (!addonIds.length) {
      return;
    }

    var addonsOnly = cart.item_count > 0 && cart.items.every(function(item) {
      return isAddon(item.product_id);
    });

    state.addonsOnly = addonsOnly;
    form.classList.toggle('cart--addons-only', addonsOnly);

    if (els.addonWarning) {
      els.addonWarning.classList.toggle('hide', !addonsOnly);
    }
  }

  /*============================================================================
    Shipping notice acknowledgment. The checkbox sits in the static drawer
    footer (never rerendered), so its state survives cart rebuilds and
    drawer open/close.
  ==============================================================================*/
  function noticeAcknowledged() {
    return !els.noticeAck || els.noticeAck.checked;
  }

  // The only place the checkout button state is set, so a cart rebuild can
  // never re-enable it while either gate is active
  function updateCheckoutState() {
    if (!els.checkoutBtn) {
      return;
    }
    var blocked = state.addonsOnly || !noticeAcknowledged();
    els.checkoutBtn.disabled = blocked;
    els.checkoutBtn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
  }

  if (els.noticeAck) {
    els.noticeAck.addEventListener('change', updateCheckoutState);
  }

  // Block drawer form submission while a checkout gate is active (Enter key, etc.)
  form.addEventListener('submit', function(evt) {
    if (state.addonsOnly || !noticeAcknowledged()) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      if (els.checkoutBtn) {
        els.checkoutBtn.classList.remove('btn--loading');
      }
      if (state.addonsOnly && els.addonWarning) {
        els.addonWarning.classList.remove('hide');
      }
    }
  }, true);

  /*============================================================================
    Delegated clicks for upsell/add-on add buttons (survive any rerender)
  ==============================================================================*/
  form.addEventListener('click', function(evt) {
    var upsellBtn = evt.target.closest('[data-upsell-add]');
    if (upsellBtn) {
      addVariant(upsellBtn.dataset.variantId, upsellBtn);
      return;
    }

    var addonBtn = evt.target.closest('[data-addon-add]');
    if (addonBtn) {
      addVariant(addonBtn.dataset.variantId, addonBtn);
    }
  });

  /*============================================================================
    Refresh orchestration
  ==============================================================================*/
  function renderExtras(cart) {
    if (!cart || cart.status) {
      return;
    }
    state.cart = cart;
    if (els.checkoutTotal) {
      els.checkoutTotal.innerHTML = cart.total_price > 0 ? ' &bull; ' + formatMoney(cart.total_price) : '';
    }
    renderShipbar(cart);
    renderUpsells(cart);
    renderAddons(cart);
    renderAddonGate(cart);
    updateCheckoutState();
    clampAddonQuantities(cart);
    restoreGiftNote(cart);
    decorateLineItems();
  }

  var refresh = debounce(function() {
    theme.cart.getCart().then(renderExtras).catch(function() { /* noop */ });
  }, 150);

  // The theme dispatches this after every Ajax quantity change with the parsed cart
  document.addEventListener('cart:updated', function(evt) {
    if (evt.detail && evt.detail.cart && !evt.detail.cart.status) {
      renderExtras(evt.detail.cart);
    } else {
      refresh();
    }
  });

  // The theme dispatches this after every Ajax add to cart (product forms, quick add, etc.)
  document.addEventListener('ajaxProduct:added', refresh);

  // Refetch recommendations every time the drawer opens so related products
  // show even if an earlier fetch failed or the catalog changed.
  document.addEventListener('drawerOpen.CartDrawer', function() {
    state.upsellSourceId = null;
    refresh();
  });

  // Initial state
  updateCheckoutState();
  refresh();
})();
